import { useActiveContextScope } from "@/features/active-context";
import { BriefingSlot } from "@/features/briefing";
import { useChatStore } from "@/features/chat";
import { FocusedViewLayout, clearFocusKeepMsg } from "@/features/sources";
import { ArtifactPane } from "@/features/sources/components/ArtifactPane";
import { GeneratingState } from "@/features/sources/components/GeneratingState";
import {
  selectDocGenerationActive,
  useDocGenerationStore,
} from "@/features/sources/state/docGenerationStore";
import { DocSyncManager } from "@/features/sync/components/DocSyncManager";
import { validateDocumentsSearch } from "@/features/workspace-router";
import { QuickAddMenu } from "@/features/workspace/components/QuickAddMenu";
import { UploadModal } from "@/features/workspace/components/UploadModal";
import { DocumentsSearchFilters } from "@/features/workspace/components/searchFilters";
import { BulkActionBar } from "@/features/workspace/documents/components/BulkActionBar";
import { DocumentsEmptyState } from "@/features/workspace/documents/components/DocumentsEmptyStates";
import {
  FolderBrowserView,
  type FolderDeleteRef,
} from "@/features/workspace/documents/components/FolderBrowserView";
import { GroupingModeToggle } from "@/features/workspace/documents/components/GroupingModeToggle";
import { MoveToFolderDialog } from "@/features/workspace/documents/components/MoveToFolderDialog";
import { VirtualizedDocList } from "@/features/workspace/documents/components/VirtualizedDocList";
import {
  type DriveSyncStatus,
  SMART_CHIP_CATALOGUE,
  type SmartChipDef,
  SmartSourceChips,
} from "@/features/workspace/documents/components/SmartSourceChips";
import { useDocumentsKeyboardNav } from "@/features/workspace/documents/hooks/useDocumentsKeyboardNav";
import {
  buildFolderTreeFromDocuments,
  parseFolderFilter,
  splitFolderPath,
} from "@/features/workspace/documents/utils/folderPath";
import {
  type RecencyBucket,
  encodeCollapsedParam,
  groupByRecency,
  isBucketExpanded,
  parseCollapsedParam,
} from "@/features/workspace/documents/utils/recencyGroup";
import {
  type SmartView,
  isPinned,
  parseSmartView,
} from "@/features/workspace/documents/utils/smartViews";
import {
  type GroupingMode,
  SOURCE_DEFAULT_EXPANDED,
  type SourceBucket,
  encodeCollapsedSourceParam,
  encodeGroupingMode,
  groupBySource,
  isSourceBucketExpanded,
  parseCollapsedSourceParam,
  parseGroupingMode,
} from "@/features/workspace/documents/utils/sourceGroup";
import {
  documentConnectionsQueryOptions,
  documentCountsQueryOptions,
  documentsInfiniteQueryOptions,
  useDocumentConnections,
  useDocumentCounts,
  useDocuments,
} from "@/features/workspace/hooks/useDocuments";
import { getSession } from "@ando/core/auth";
import { useEditSession } from "@/features/workspace/hooks/useEditSession";
import { useMoveDocument } from "@/features/workspace/hooks/useMoveDocument";
import { useSearchNavigation } from "@/features/workspace/hooks/useSearchNavigation";
import { useTogglePinDocument } from "@/features/workspace/hooks/useTogglePinDocument";
import { trackIngestionToast } from "@/features/workspace/lib/ingestionToast";
import {
  type DriveProvider,
  type UploadDocumentResponse,
  type WorkspaceDocument,
  deleteDocument,
} from "@ando/core";
import type { AndoSource } from "@ando/core/domain";
import { type WebPayload, WebPreview } from "@ando/ui";
import {
  SurfaceCommandBar,
  SurfaceEmptyState,
  SurfaceFilterSection,
  SurfaceFiltersPopover,
  SurfaceLayout,
  WorkspaceSearchBar,
  toast,
} from "@ando/ui";
import { confirm } from "@ando/ui/dialog";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { FileText } from "lucide-react";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";

export const Route = createFileRoute("/_authed/workspace/documents")({
  validateSearch: validateDocumentsSearch,
  // Search-dependent preload — re-runs when view/folder/q change.
  loaderDeps: ({ search }) => ({
    view: search.view,
    folder: search.folder,
    q: search.q,
  }),
  // Warm the FIRST page of the documents list on hover/focus. userId is read
  // synchronously from the session (no React); the filter derivation mirrors
  // DocumentsHub exactly (parseSmartView + folder + trimmed q + the source
  // whitelist) so the loader hits the SAME cache key the hub mounts with.
  loader: ({ context: { queryClient }, deps }) => {
    const userId = getSession()?.user_id ?? null;
    if (!userId) return;
    void queryClient.ensureInfiniteQueryData(
      documentsInfiniteQueryOptions(userId, {
        limit: 100,
        sourceFilter: DOCUMENT_SOURCE_WHITELIST_ARRAY,
        view: parseSmartView(deps.view),
        folder: deps.folder,
        q: typeof deps.q === "string" ? deps.q.trim() || undefined : undefined,
      }),
    );
    // The hub also fires the chip counts + connection picker on mount — warm
    // both so they paint from cache instead of spinning after navigation.
    void queryClient.ensureQueryData(documentCountsQueryOptions(userId));
    void queryClient.ensureQueryData(documentConnectionsQueryOptions());
  },
  component: DocumentsHub,
});

/**
 * Documents hub — SOTA redesign (June 2026, post-feedback pivot).
 *
 * Grouping
 * --------
 *  - DEFAULT axis = **source** (Google Drive / OneDrive / SharePoint /
 *    Email / Chat / Manual / Generati da Ando / Filesystem / Altro),
 *    chosen after user feedback: "Tipo Google Drive/*** OneDrive/**
 *    insomma in base alla fonte". Folder hierarchy not yet exposed by
 *    the BE → we group at the source level only; the module is wired so
 *    nested folders drop in when ``parent_folder_id`` lands.
 *  - SECONDARY axis = recency (Oggi / Ultimi 7g / Questo mese / Più
 *    vecchi). Toggle via :class:`GroupingModeToggle` in the header.
 *
 * Filter strip
 * ------------
 * The existing :class:`DocumentsSearchFilters` chip strip + the 5-way
 * :class:`SmartSourceChips` row replace the previous "Filtri avanzati"
 * drawer (removed per user feedback: "La parte filtri avanzagti sopra
 * non mi serve! Ho già la bar dei filtri!"). Provider granularity stays
 * available via the smart-view chips.
 *
 * Selection / keyboard
 * --------------------
 *  - DocumentCardV2 with MIME-tone icon, hover-checkbox, kebab menu.
 *  - Bulk-select with sticky bottom :class:`BulkActionBar` (Sposta
 *    removed — no folder destination concept yet).
 *  - Keyboard nav: ``/`` focus search · j/k or ↓/↑ move · Enter open ·
 *    Space toggle · Cmd/Ctrl+A select-all · Esc deselect.
 *
 * Backward-compat: ``/workspace/documents?focus=<doc_id>`` still routes
 * to :class:`ArtifactPane` inline editor.
 */

const DOCUMENT_SOURCE_WHITELIST_ARRAY: ReadonlyArray<WorkspaceDocument["source_type"]> = [
  "drive",
  "manual_upload",
  "email_attachment",
  "chat",
  "ai_generated",
  "filesystem",
];
const DOCUMENT_SOURCE_WHITELIST: ReadonlySet<WorkspaceDocument["source_type"]> = new Set(
  DOCUMENT_SOURCE_WHITELIST_ARRAY,
);


function DocumentsHub() {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  // ``setBriefingHasContent`` still feeds ``BriefingSlot onLoaded``; the value
  // itself is no longer read (its only consumer was the CompanionHero, removed
  // with the Jul 2026 command-bar de-layering).
  const [, setBriefingHasContent] = useState(false);
  const search = useSearch({ from: "/_authed/workspace/documents" });
  const searchNav = useSearchNavigation<{
    source: ReadonlyArray<string>;
    mime: ReadonlyArray<string>;
    period: string;
    mode: string;
  }>("/workspace/documents");

  const focusId = search.focus ?? null;
  const messageId = (search as { msg?: string }).msg ?? null;
  // An unambiguous-source document authoring run (create/fork/edit) emits an
  // early ``doc_generation_started`` WS event but no fork exists yet to focus —
  // so when active AND no doc is focused, the work-area shows the branded
  // generating loader instead of a "nothing happening" list (Jun 22, 2026).
  const docGenActive = useDocGenerationStore(selectDocGenerationActive);
  const docGenTitle = useDocGenerationStore((s) => s.title);
  const activeView: SmartView = parseSmartView(search.view);
  const groupingMode: GroupingMode = parseGroupingMode(
    typeof search.group === "string" ? search.group : undefined,
  );
  const folderFilter = useMemo(
    () => parseFolderFilter(typeof search.folder === "string" ? search.folder : undefined),
    [search.folder],
  );
  const collapsedSet = useMemo(
    () =>
      parseCollapsedParam(
        typeof search.collapsed === "string"
          ? search.collapsed
          : Array.isArray(search.collapsed)
            ? (search.collapsed as readonly string[]).join(",")
            : undefined,
      ),
    [search.collapsed],
  );
  const collapsedSourceSet = useMemo(() => {
    const raw =
      typeof search.collapsed_source === "string"
        ? search.collapsed_source
        : Array.isArray(search.collapsed_source)
          ? (search.collapsed_source as readonly string[]).join(",")
          : undefined;
    if (raw !== undefined) return parseCollapsedSourceParam(raw);
    // First-visit default: collapse every bucket NOT in the default-
    // expanded list, so the page opens with the four cloud + AI groups
    // already on without forcing the user to set URL state.
    const defaults = new Set<SourceBucket>([
      "google_drive",
      "onedrive",
      "sharepoint",
      "email",
      "chat",
      "manual_upload",
      "filesystem",
      "ai_generated",
      "other",
    ]);
    for (const id of SOURCE_DEFAULT_EXPANDED) defaults.delete(id);
    return defaults as ReadonlySet<SourceBucket>;
  }, [search.collapsed_source]);

  const searchQuery: string = typeof search.q === "string" ? search.q : "";

  // ── Server-side filter pipeline (Jun 24, 2026 SOTA) ──────────────────
  // Every smart-view / folder / search / pinned filter is passed to the
  // BE which returns ONLY the matching rows. The FE no longer iterates
  // over a global "all documents" set — the chip badge counts come from
  // ``useDocumentCounts`` (separate single-roundtrip endpoint). This
  // scales the hub to 100k+ documents because the per-filter result set
  // is small relative to the global table.
  //
  // ``filteredDocs`` is the SAME as ``documents`` here — kept as an
  // alias so the existing rendering code (recency/source/folder groups)
  // doesn't need to be touched.
  const {
    documents: rawDocuments,
    total: rawTotal,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useDocuments(
    {
      limit: 100,
      sourceFilter: DOCUMENT_SOURCE_WHITELIST_ARRAY,
      view: activeView,
      folder: search.folder,
      q: searchQuery.trim() || undefined,
    },
    // Virtualized hub — pages load on scroll via the list's ``onLoadMore``.
    { autoFetchAll: false },
  );

  const { counts: documentCounts } = useDocumentCounts();
  const { connections: driveConnections, isLoading: loadingDrive } = useDocumentConnections();

  // Drive sync status — derive once for the "Drive" chip dot. Errored
  // connections (``needs_reauth``) win, then "loading" while the BE list
  // is in flight, then OK when at least one real drive is connected.
  const driveSyncStatus: DriveSyncStatus = useMemo(() => {
    if (loadingDrive) return "running";
    const realDrives = driveConnections.filter((c) => c.vendor !== "manual");
    if (realDrives.length === 0) return "none";
    if (realDrives.some((c) => c.needs_reauth)) return "error";
    return "ok";
  }, [driveConnections, loadingDrive]);

  // Belt-and-braces FE whitelist (the BE already enforces it via
  // ``source_filter`` but keep the predicate as a defensive guard).
  const documents = useMemo(
    () => rawDocuments.filter((d) => DOCUMENT_SOURCE_WHITELIST.has(d.source_type)),
    [rawDocuments],
  );

  // ``filteredDocs`` is just ``documents`` post-server-side-filtering —
  // kept as a named alias so the rendering code below reads as
  // "show me what passed the filters". The folder browser receives
  // the same set since the ``folder`` query param already narrowed it.
  const filteredDocs = documents;

  // Per-chip badge counts come from the BE counts endpoint — accurate
  // even when only a filtered slice of documents is loaded.
  const chipDefs: readonly SmartChipDef[] = useMemo(() => {
    return SMART_CHIP_CATALOGUE.map((c) => ({
      ...c,
      badge: documentCounts[c.id] ?? 0,
    }));
  }, [documentCounts]);

  // Folder tree derived from the loaded documents (post-Jun 24
  // consolidation). Built BEFORE the folder filter is applied so the
  // sidebar shows the full hierarchy regardless of which folder is
  // currently selected — clicking a folder narrows the row list, not
  // the rail. Empty providers are filtered out by the rail itself.
  const folderTree = useMemo(() => buildFolderTreeFromDocuments(documents), [documents]);

  // Pre-sort documents newest-first by created_at so the WITHIN-bucket
  // order is stable regardless of grouping axis.
  const sortedDocs = useMemo(() => {
    const copy = filteredDocs.slice();
    copy.sort((a, b) => {
      const ta = a.created_at ? Date.parse(a.created_at) : 0;
      const tb = b.created_at ? Date.parse(b.created_at) : 0;
      return tb - ta;
    });
    return copy;
  }, [filteredDocs]);

  // Grouping — recency OR source, picked off the URL ``?group=`` param.
  const recencyGroups = useMemo(
    () => (groupingMode === "recency" ? groupByRecency(sortedDocs) : []),
    [sortedDocs, groupingMode],
  );
  const sourceGroups = useMemo(
    () => (groupingMode === "source" ? groupBySource(sortedDocs) : []),
    [sortedDocs, groupingMode],
  );

  // Flat list of visible ids (post-collapse) — drives keyboard nav.
  const visibleIds = useMemo<readonly string[]>(() => {
    const out: string[] = [];
    if (groupingMode === "recency") {
      for (const group of recencyGroups) {
        if (!isBucketExpanded(group.bucket, collapsedSet)) continue;
        for (const item of group.items) {
          if (item.doc_id) out.push(item.doc_id);
        }
      }
    } else {
      for (const group of sourceGroups) {
        if (!isSourceBucketExpanded(group.bucket, collapsedSourceSet)) continue;
        for (const item of group.items) {
          if (item.doc_id) out.push(item.doc_id);
        }
      }
    }
    return out;
  }, [groupingMode, recencyGroups, sourceGroups, collapsedSet, collapsedSourceSet]);

  // ── Selection state ──────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const toggleSelect = useCallback((docId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  }, []);
  const deselectAll = useCallback(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);
  const selectAllVisible = useCallback(() => {
    setSelectedIds(new Set(visibleIds));
  }, [visibleIds]);

  // ── URL navigators ──────────────────────────────────────────────────
  const setActiveView = useCallback(
    (next: SmartView) => {
      navigate({
        to: "/workspace/documents",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          view: next === "tutti" ? undefined : next,
        }),
      } as Parameters<typeof navigate>[0]);
    },
    [navigate],
  );

  const setGroupingMode = useCallback(
    (next: GroupingMode) => {
      navigate({
        to: "/workspace/documents",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          group: encodeGroupingMode(next),
        }),
      } as Parameters<typeof navigate>[0]);
    },
    [navigate],
  );

  // Folder deep-link — drives ``?folder=<provider>::<path>``. ``null``
  // clears the filter. Pattern mirrors :func:`setGroupingMode`: a single
  // ``navigate`` patch on the search object so the back button rewinds
  // the filter selection without losing other URL state (search, view,
  // collapsed sets).
  const setFolderFilter = useCallback(
    (next: string | null) => {
      navigate({
        to: "/workspace/documents",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          folder: next ?? undefined,
        }),
      } as Parameters<typeof navigate>[0]);
    },
    [navigate],
  );

  const toggleBucket = useCallback(
    (bucket: RecencyBucket) => {
      const next = new Set(collapsedSet);
      if (next.has(bucket)) next.delete(bucket);
      else next.add(bucket);
      const encoded = encodeCollapsedParam(next);
      navigate({
        to: "/workspace/documents",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          collapsed: encoded,
        }),
      } as Parameters<typeof navigate>[0]);
    },
    [collapsedSet, navigate],
  );

  const toggleSourceBucket = useCallback(
    (bucket: SourceBucket) => {
      const next = new Set(collapsedSourceSet);
      if (next.has(bucket)) next.delete(bucket);
      else next.add(bucket);
      const encoded = encodeCollapsedSourceParam(next);
      navigate({
        to: "/workspace/documents",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          collapsed_source: encoded,
        }),
      } as Parameters<typeof navigate>[0]);
    },
    [collapsedSourceSet, navigate],
  );

  // ── Open / focus ─────────────────────────────────────────────────────
  const openDoc = useCallback(
    (docId: string) => {
      navigate({
        to: "/workspace/documents",
        search: { focus: docId },
      });
    },
    [navigate],
  );

  // ── Keyboard nav ─────────────────────────────────────────────────────
  const focusSearch = useCallback(() => {
    const root = typeof document !== "undefined" ? document : null;
    if (!root) return;
    const input = root.querySelector<HTMLInputElement>(
      `input[aria-label="${i18n._(msg`Cerca documenti`)}"]`,
    );
    if (input) {
      input.focus();
      input.select?.();
    }
  }, [i18n]);

  const { focusedId } = useDocumentsKeyboardNav({
    visibleIds,
    onOpen: openDoc,
    onToggleSelect: toggleSelect,
    onSelectAll: selectAllVisible,
    onDeselectAll: deselectAll,
    onFocusSearch: focusSearch,
    disabled: focusId !== null,
  });

  // ── Upload modal plumbing ───────────────────────────────────────────
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const handleUploadClick = () => setUploadModalOpen(true);

  // ── Drive picker plumbing (QuickAddMenu → DriveFolderPicker) ────────
  // Single-instance picker: the open ``provider`` doubles as both the
  // routing key for the picker and the open/close flag — ``null`` means
  // closed. Connection key flows through alongside so multi-account
  // Google Drive routes pick the right token without a second probe.
  const [drivePickerState, setDrivePickerState] = useState<{
    readonly provider: DriveProvider;
    readonly connectionKey?: string;
  } | null>(null);
  const handlePickDrive = useCallback(
    (provider: DriveProvider, connectionKey?: string) =>
      setDrivePickerState({ provider, connectionKey }),
    [],
  );
  const handleConfigureConnectors = useCallback(
    () => navigate({ to: "/integrations" }),
    [navigate],
  );
  // Spreadsheet has a 3-source wizard (local + Google Drive + OneDrive)
  // via the capability hub — funnel the user there instead of the
  // generic UploadModal so they can pick the cloud path too.
  const handleAddSpreadsheet = useCallback(
    () =>
      navigate({
        to: "/integrations/$capability",
        params: { capability: "spreadsheet" },
      }),
    [navigate],
  );
  const handleUploaded = (responses: readonly UploadDocumentResponse[]) => {
    // Immediate refresh so the new rows appear (with their "in corso"
    // ingestion badges), then a live progress toast that re-invalidates
    // once the async Temporal ingestion settles.
    queryClient.invalidateQueries({ queryKey: ["workspace", "documents"] });
    trackIngestionToast(responses, {
      onSettled: () => queryClient.invalidateQueries({ queryKey: ["workspace", "documents"] }),
    });
  };

  // ── Delete (cascade across Weaviate + Neo4j + DB + MinIO) ───────────
  //
  // The BE ``DELETE /documents/{source_file}?cascade=true`` routes
  // through ``CascadeDeleteService`` — the SAME helper already wired
  // for admin tooling. ``WorkspaceDocument.filename`` is the BE key
  // (``content_items.filename``) the cascade joins on; ``doc_id`` is
  // FE-only display id. We invalidate the same ``["workspace",
  // "documents"]`` query the upload flow uses so the row vanishes from
  // every grouping the route paints (recency + source).
  // In-flight delete tracking — drives the dim-row "Eliminazione…"
  // visual on the matching ``<DocumentCardV2>``. Notion / Vercel
  // pattern: the row STAYS in the list, dimmed + with a destructive-
  // tone spinner pill, until the cascade settles + the query
  // invalidates. Picked over an optimistic remove (Linear) because
  // the BE cascade is IRREVERSIBLE — claiming "done" before the
  // backend confirms would mean a misleading checkmark + stranded
  // row on failure.
  const [deletingIds, setDeletingIds] = useState<Set<string>>(() => new Set());

  // Jun 29, 2026 — toast suppression set per batch delete folder.
  // Quando l'utente cancella una cartella, il batch dispatcha N
  // ``deleteMutation.mutate(file)`` riusando lo stesso path del delete
  // singolo (DRY). Senza filtro otterresti 1 toast aggregato + N toast
  // per file (rumorosa). Marca-tutti-batch ⇒ skip toast singoli.
  // Ref invece di state per evitare re-render del listing su ogni
  // start/end di un file del batch (con 50+ file il listing sarebbe
  // re-renderato 100+ volte solo per il bookkeeping del toast).
  const batchSilentIdsRef = React.useRef<Set<string>>(new Set());

  const deleteMutation = useMutation({
    mutationFn: (doc: WorkspaceDocument) => deleteDocument(doc.filename),
    onMutate: (doc) => {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.add(doc.doc_id);
        return next;
      });
    },
    onSuccess: (data, doc) => {
      const trackedRemoved = data.deleted?.drive_tracked_files ?? 0;
      if (!batchSilentIdsRef.current.has(doc.doc_id)) {
        const description =
          trackedRemoved > 0
            ? i18n._(msg`Rimosso dall'indice, dal grafo, dallo storage e dai file sincronizzati.`)
            : i18n._(msg`Rimosso dall'indice, dal grafo e dallo storage.`);
        toast.success(i18n._(msg`"${doc.title || doc.filename}" eliminato`), { description });
      }
      // Invalidate the documents list AND, when the file lived in
      // ``drive_tracked_files``, the Drive sync caches so the
      // "Gestisci file" modal doesn't keep showing the file as
      // tracked (June 10, 2026 user feedback).
      void queryClient.invalidateQueries({
        queryKey: ["workspace", "documents"],
      });
      if (trackedRemoved > 0) {
        void queryClient.invalidateQueries({ queryKey: ["drive"] });
      }
    },
    onError: (err, doc) => {
      // Errori arrivano SEMPRE all'utente, anche in batch — un fail
      // silente è peggio del rumore.
      toast.error(
        i18n._(
          msg`Impossibile eliminare "${doc.title || doc.filename}": ${err instanceof Error ? err.message : i18n._(msg`errore`)}`,
        ),
      );
    },
    onSettled: (_data, _err, doc) => {
      setDeletingIds((prev) => {
        if (!prev.has(doc.doc_id)) return prev;
        const next = new Set(prev);
        next.delete(doc.doc_id);
        return next;
      });
      batchSilentIdsRef.current.delete(doc.doc_id);
    },
  });

  const handleDeleteDocument = async (doc: WorkspaceDocument) => {
    const ok = await confirm({
      title: i18n._(msg`Eliminare "${doc.title || doc.filename}"?`),
      description: i18n._(
        msg`Il file verrà rimosso dall'indice vettoriale, dal grafo della conoscenza, dal database e dallo storage. Operazione irreversibile.`,
      ),
      confirmLabel: i18n._(msg`Elimina`),
      cancelLabel: i18n._(msg`Annulla`),
      destructive: true,
    });
    if (!ok) return;
    deleteMutation.mutate(doc);
  };

  // Jun 29, 2026 — delete intera sotto-cartella (cascade su tutti i
  // file diretti + descendant). DRY zero-cost: filtra i docs visibili
  // per provider + crumbs e fa batch ``deleteMutation.mutate(file)``
  // riusando il path esistente per il delete singolo (cascade BE
  // identico via ``DELETE /documents/{filename}?cascade=true``).
  // Niente nuovo endpoint, niente nuovo hook.
  //
  // ⚠️ KNOWN LIMITATION (Jun 29, 2026): se la cartella è una
  // ``drive_tracked_folders`` root (es. "Test - Brasile" tracked
  // intero dal sync drawer), il batch-delete cancella i singoli
  // content_items ma NON marca il tracking row come ``is_tracked=FALSE``.
  // Al prossimo Drive sync i file vengono re-ingested.
  // Fix proprio: l'endpoint BE ``DELETE /tracked-folders/{id}`` (con
  // nuovo default cascade=true) va chiamato per le tracked-root. PR
  // futura: estendere ``FolderDeleteRef`` con ``trackedFolderId?:
  // string`` calcolato dai docs (via ``drive_folder_id`` su un file
  // child) — se valorizzato, handler chiama ``removeTrackedFolder``
  // invece del batch file delete.
  const handleDeleteFolder = async (ref: FolderDeleteRef) => {
    // Use ``filteredDocs`` (the same set the browser computes
    // ``cumulative_count`` from) — using raw ``documents`` would risk
    // blast-radius > count quando un filtro smart-view è attivo.
    const filesToDelete = filteredDocs.filter((d) => {
      if (d.drive_provider !== ref.drive_provider) return false;
      const docCrumbs = splitFolderPath(d.folder_path);
      if (docCrumbs.length < ref.absoluteCrumbs.length) return false;
      return ref.absoluteCrumbs.every((c, i) => docCrumbs[i] === c);
    });

    if (filesToDelete.length === 0) {
      toast.info(i18n._(msg`La cartella "${ref.name}" è già vuota.`));
      return;
    }

    const ok = await confirm({
      title: i18n._(msg`Eliminare la cartella "${ref.name}"?`),
      description: i18n._(
        msg`${filesToDelete.length} file (inclusi i contenuti delle sotto-cartelle) verranno rimossi dall'indice vettoriale, dal grafo, dal database e dallo storage. Operazione irreversibile.`,
      ),
      confirmLabel: i18n._(msg`Elimina ${filesToDelete.length} file`),
      cancelLabel: i18n._(msg`Annulla`),
      destructive: true,
    });
    if (!ok) return;

    // Mark all batch IDs as silent BEFORE dispatching: ``onSuccess``
    // del mutation skip-pa il toast per-file se l'id è nel ref. UX:
    // 1 toast aggregato di avvio + 1 toast aggregato finale via
    // Promise.all → no spam (con 50 file evitiamo 50 toast).
    filesToDelete.forEach((d) => batchSilentIdsRef.current.add(d.doc_id));

    toast.info(i18n._(msg`Eliminazione cartella "${ref.name}"`), {
      description: i18n._(msg`${filesToDelete.length} file in coda…`),
    });

    // BOUNDED CONCURRENCY (Jun 30, 2026 code-review): un ``Promise.allSettled``
    // ingenuo su 100+ file lancerebbe N DELETE HTTP simultanei → pool
    // connessioni BE saturo (asyncpg, Weaviate gRPC), rischio 429
    // silenti, e 2N re-render del listing (1 ``onMutate`` + 1
    // ``onSettled`` per file). Chunk di 8 = sweet spot: copre i 4
    // CPU core tipici BE + lascia spazio per richieste user-facing
    // concorrenti, eppure smaltisce 80 file in ~10 round (≈30s con
    // p95=3s per cascade-delete).
    const CONCURRENCY = 8;
    const results: PromiseSettledResult<unknown>[] = [];
    for (let i = 0; i < filesToDelete.length; i += CONCURRENCY) {
      const chunk = filesToDelete.slice(i, i + CONCURRENCY);
      const chunkResults = await Promise.allSettled(
        chunk.map((doc) => deleteMutation.mutateAsync(doc)),
      );
      results.push(...chunkResults);
    }
    const failed = results.filter((r) => r.status === "rejected").length;
    const succeeded = results.length - failed;
    if (failed === 0) {
      toast.success(i18n._(msg`Cartella "${ref.name}" eliminata`), {
        description: i18n._(msg`${succeeded} file rimossi.`),
      });
    } else if (succeeded === 0) {
      toast.error(i18n._(msg`Eliminazione cartella "${ref.name}" fallita`), {
        description: i18n._(msg`Nessuno dei ${failed} file rimosso. Riprova.`),
      });
    } else {
      toast.warning(i18n._(msg`Cartella "${ref.name}" eliminata parzialmente`), {
        description: i18n._(msg`${succeeded} ok · ${failed} falliti.`),
      });
    }
  };

  // ── Pin / unpin (Jun 11, 2026) ───────────────────────────────────────
  // Server-side pin flag — see ``useTogglePinDocument`` for the
  // optimistic-update + silent-rollback contract. The callback computes
  // the target state from the current row so the same handler covers
  // both pin AND un-pin paths (idempotent on the BE).
  const { togglePin } = useTogglePinDocument();
  const handleTogglePinDocument = useCallback(
    (doc: WorkspaceDocument) => {
      void togglePin(doc.doc_id, !isPinned(doc));
    },
    [togglePin],
  );

  // ── Move to virtual folder (Jun 24, 2026) ────────────────────────────
  // Non-Drive rows can be relocated into user-defined folders via
  // ``content_items.virtual_folder_path``. Drive rows are read-only
  // mirrors and the BE rejects move attempts with 409 — we therefore
  // wire ``onMove`` ONLY for non-Drive sources at the card render site.
  const { move: moveDocument, isPending: isMoving } = useMoveDocument();
  const [movingDoc, setMovingDoc] = useState<WorkspaceDocument | null>(null);
  const handleMoveDocument = useCallback((doc: WorkspaceDocument) => {
    setMovingDoc(doc);
  }, []);
  const handleConfirmMove = useCallback(
    async (folderPath: string | null) => {
      if (!movingDoc) return;
      await moveDocument(movingDoc.doc_id, folderPath);
      setMovingDoc(null);
    },
    [moveDocument, movingDoc],
  );

  const goToIntegrations = () => navigate({ to: "/integrations" });

  // ── Meta line ────────────────────────────────────────────────────────
  // ``rawTotal`` is the BE's TRUE ``COUNT(*) OVER ()`` for the current filter
  // (never capped by the LIMIT/OFFSET page window). The old ``Math.min`` with
  // ``documents.length`` clamped the total to the auto-fetch cap (10 pages ×
  // 100 = 1000), so a library with 1096 rows read the confusing "1000 di
  // 1000". Show the real total instead → "1000 di 1096" (loaded di totale).
  const displayTotal = rawTotal;
  const filteredCount = filteredDocs.length;

  // ── Virtualized list wiring (recency / source modes) ──────────────────
  // The scroll element is SurfaceLayout's content section (``contentRef``).
  const scrollRef = React.useRef<HTMLElement>(null);
  const virtualGroups = groupingMode === "recency" ? recencyGroups : sourceGroups;
  const isVirtualBucketExpanded = useCallback(
    (bucket: string) =>
      groupingMode === "recency"
        ? isBucketExpanded(bucket as RecencyBucket, collapsedSet)
        : isSourceBucketExpanded(bucket as SourceBucket, collapsedSourceSet),
    [groupingMode, collapsedSet, collapsedSourceSet],
  );
  const toggleVirtualBucket = useCallback(
    (bucket: string) =>
      groupingMode === "recency"
        ? toggleBucket(bucket as RecencyBucket)
        : toggleSourceBucket(bucket as SourceBucket),
    [groupingMode, toggleBucket, toggleSourceBucket],
  );

  // ── Empty-state context ──────────────────────────────────────────────
  // Jun 30, 2026 — quando l'utente è in modalità "folder" CON un filtro
  // ``folder=...`` URL attivo (cioè ha drillato dentro una cartella),
  // il fatto che ``documents.length === 0`` significa "cartella vuota",
  // NON "l'utente non ha file". Mostrare il default "all_empty" /
  // "search_empty" (con CTA Upload/Connect Drive) ingannava: l'utente
  // ha 500+ file altrove, semplicemente questa folder è vuota (es. ha
  // appena cancellato l'unica sub-cartella). In quel caso ritorna null
  // così il render delega a ``FolderBrowserView`` che ha il SUO empty
  // state contestuale (con CTA "← Torna a {parent}").
  const isFolderDrilled = groupingMode === "folder" && folderFilter !== null;
  const emptyContext = useMemo(() => {
    if (searchQuery.trim().length > 0 && filteredCount === 0) {
      return { kind: "search_empty" as const, query: searchQuery.trim() };
    }
    if (activeView === "drive") {
      if (driveSyncStatus === "none") {
        return { kind: "drive_disconnected" as const };
      }
      if (driveSyncStatus === "running" && filteredCount === 0) {
        return { kind: "drive_syncing" as const };
      }
    }
    if (activeView === "ricevuti" && filteredCount === 0) {
      return { kind: "received_empty" as const };
    }
    if (isFolderDrilled) {
      // Folder filter URL attivo + 0 docs → delega al FolderBrowserView.
      return null;
    }
    if (documents.length === 0) {
      return { kind: "all_empty" as const };
    }
    if (filteredCount === 0) {
      return { kind: "search_empty" as const, query: "" };
    }
    return null;
  }, [searchQuery, filteredCount, activeView, driveSyncStatus, documents.length, isFolderDrilled]);

  // ── Inline-viewer mode ──────────────────────────────────────────────
  if (focusId) {
    return (
      <FocusedDocumentView
        focusId={focusId}
        messageId={messageId}
        onBack={() =>
          navigate({
            to: "/workspace/documents",
            search: clearFocusKeepMsg(messageId),
          })
        }
      />
    );
  }

  // ── Generating mode (no doc to focus yet) ───────────────────────────
  // A no-confirmation authoring run is in flight; surface the branded loader
  // in the focused-view chrome until the redline proposal lands and routes
  // the user to the new fork (``useActionRouter`` clears ``docGenActive``).
  if (docGenActive) {
    return (
      <FocusedViewLayout
        tabLabel={i18n._(msg`Documenti`)}
        eyebrowSuffix={i18n._(msg`Generazione`)}
        title={docGenTitle ?? i18n._(msg`Nuovo documento`)}
        icon={<FileText className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />}
        backAriaLabel={i18n._(DOCS_BACK_LABEL_MSG)}
        messageId={messageId}
        focusId={null}
      >
        <GeneratingState />
      </FocusedViewLayout>
    );
  }

  const meta = isLoading ? (
    <span className="text-ink-3">
      <Trans>Caricamento…</Trans>
    </span>
  ) : (
    <>
      <strong>
        <Trans>
          <span className="tabular-nums">{filteredCount}</span> di {displayTotal} file
        </Trans>
      </strong>
    </>
  );

  return (
    <>
      <SurfaceLayout
        contentRef={scrollRef}
        header={
          // Jul 2026 — single-row command bar (replaces stacked header + hero +
          // search + filter rows). The nav tab names the area; ``count`` carries
          // the "X di Y file" summary; filters (smart-view + Tipo/Periodo) live
          // behind the shared Filtri popover.
          <SurfaceCommandBar
            count={meta}
            search={
              <WorkspaceSearchBar
                placeholder={i18n._(msg`Cerca documenti, allegati, drive…`)}
                ariaLabel={i18n._(msg`Cerca documenti`)}
                initialQuery={searchQuery}
                onSearch={searchNav.setQuery}
              />
            }
            views={
              <SmartSourceChips
                activeView={activeView}
                onChange={setActiveView}
                chips={chipDefs}
                driveSyncStatus={driveSyncStatus}
                className="!px-0 !py-0"
              />
            }
            filters={
              <SurfaceFiltersPopover
                ariaLabel={i18n._(msg`Filtri documenti`)}
                label={<Trans>Filtri</Trans>}
                activeCount={
                  (search.source?.length ?? 0) +
                  (search.mime?.length ?? 0) +
                  ((search.period ?? "all") !== "all" ? 1 : 0)
                }
              >
                {/* "Raggruppa per" moved off the command-bar actions row (Jul
                    2026) so the bar fits ONE line — it's an axis, not an action. */}
                <SurfaceFilterSection label={<Trans>Raggruppa per</Trans>}>
                  <GroupingModeToggle value={groupingMode} onChange={setGroupingMode} />
                </SurfaceFilterSection>
                <SurfaceFilterSection label={<Trans>Filtri</Trans>}>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <DocumentsSearchFilters
                      source={search.source ?? []}
                      mime={search.mime ?? []}
                      period={search.period ?? "all"}
                      mode={search.mode ?? "lexical"}
                      onChange={searchNav.patchFilters}
                    />
                  </div>
                </SurfaceFilterSection>
              </SurfaceFiltersPopover>
            }
            actions={
              // QuickAddMenu folds uploads / drive-picker / email-sync into ONE
              // "+ Nuovo" menu — the single primary, per the command-bar contract.
              <QuickAddMenu
                area="documents"
                label={i18n._(msg`Nuovo`)}
                ariaLabel={i18n._(msg`Aggiungi documenti`)}
                onUpload={handleUploadClick}
                onAddSpreadsheet={handleAddSpreadsheet}
                onPickDrive={handlePickDrive}
                onConfigureConnectors={handleConfigureConnectors}
              />
            }
          />
        }
      >
        <BriefingSlot domain="documents" className="mb-5" onLoaded={setBriefingHasContent} />
        {/* sr-only live region — announces filter result count + selection count. */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {isLoading
            ? i18n._(msg`Caricamento documenti in corso`)
            : selectedIds.size > 0
              ? i18n._(msg`${filteredCount} documenti trovati, ${selectedIds.size} selezionati`)
              : i18n._(msg`${filteredCount} documenti trovati`)}
        </div>

        {isError ? (
          <SurfaceEmptyState
            icon={<FileText size={18} strokeWidth={1.75} />}
            title={i18n._(msg`Impossibile caricare i documenti`)}
            description={i18n._(msg`Verifica la connessione e riprova fra qualche secondo.`)}
          />
        ) : isLoading && filteredCount === 0 ? (
          <DocumentsSkeleton />
        ) : emptyContext ? (
          <DocumentsEmptyState
            context={emptyContext}
            onUpload={emptyContext.kind === "all_empty" ? handleUploadClick : undefined}
            onConnectDrive={
              emptyContext.kind === "all_empty" || emptyContext.kind === "drive_disconnected"
                ? goToIntegrations
                : undefined
            }
            onClearSearch={
              emptyContext.kind === "search_empty" ? () => searchNav.setQuery("") : undefined
            }
          />
        ) : groupingMode === "folder" ? (
          /* Jun 24, 2026 — Finder-style folder browser. Drills into
             the loaded documents via ``buildFolderBrowserLevel`` (no
             extra BE call). Receives the smart-view/search-filtered
             set; folder narrowing is handled internally by the view's
             breadcrumb + drill-down nav. */
          <FolderBrowserView
            documents={filteredDocs}
            activeFilter={folderFilter}
            onSelectFolder={setFolderFilter}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onOpen={openDoc}
            onDelete={(doc) => void handleDeleteDocument(doc)}
            onDeleteFolder={(ref) => void handleDeleteFolder(ref)}
            onTogglePin={handleTogglePinDocument}
            onMove={handleMoveDocument}
            isPinnedFn={isPinned}
            deletingIds={deletingIds}
            focusedId={focusedId}
          />
        ) : (
          <VirtualizedDocList
            scrollRef={scrollRef}
            groups={virtualGroups}
            isExpanded={isVirtualBucketExpanded}
            onToggleBucket={toggleVirtualBucket}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onOpen={openDoc}
            onDelete={(doc) => void handleDeleteDocument(doc)}
            onTogglePin={handleTogglePinDocument}
            onMove={handleMoveDocument}
            isPinned={isPinned}
            deletingIds={deletingIds}
            focusedId={focusedId}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            ariaLabel={i18n._(msg`File aziendali recenti`)}
          />
        )}
      </SurfaceLayout>

      <BulkActionBar count={selectedIds.size} onDeselectAll={deselectAll} />

      {/* Move dialog (Jun 24, 2026) — only mounts when a manual/AI
          document is being relocated. The suggestion list is derived
          from the user's existing virtual folders so the picker offers
          one-click moves to already-used paths. */}
      <MoveToFolderDialog
        open={movingDoc !== null}
        onOpenChange={(open) => {
          if (!open) setMovingDoc(null);
        }}
        currentPath={movingDoc?.virtual_folder_path ?? movingDoc?.folder_path ?? null}
        suggestions={Object.values(
          buildFolderTreeFromDocuments(
            documents
              .filter((d) => d.source_type !== "drive" && d.virtual_folder_path)
              .map((d) => ({
                ...d,
                // Re-cast virtual path into the ``folder_path`` /
                // ``drive_provider`` shape ``buildFolderTreeFromDocuments``
                // expects, so we get a unified tree of manual folders.
                folder_path: d.virtual_folder_path ?? null,
                drive_provider: "manual" as const,
              })),
          ),
        ).flat()}
        onSubmit={(path) => void handleConfirmMove(path)}
        isSubmitting={isMoving}
      />

      <UploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onUploaded={handleUploaded}
      />

      {drivePickerState ? (
        <DocSyncManager
          open
          onOpenChange={(next) => {
            if (!next) {
              setDrivePickerState(null);
              // The DocSyncManager surfaces "Sincronizza" actions
              // inline; on close we re-query so any newly synced item
              // appears immediately in the source-grouped view.
              void queryClient.invalidateQueries({
                queryKey: ["workspace", "documents"],
              });
            }
          }}
          provider={drivePickerState.provider}
          connectionKey={drivePickerState.connectionKey}
        />
      ) : null}
    </>
  );
}

/** Lightweight 6-row skeleton — matches DocumentCardV2 height. */
const DocumentsSkeleton: React.FC = () => {
  const { i18n } = useLingui();
  return (
    <ul
      aria-busy="true"
      aria-label={i18n._(msg`Caricamento documenti`)}
      className="flex flex-col gap-1"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <li
          key={`docs-skel-${i}`}
          className="flex items-center gap-3 rounded-lg border border-hairline bg-white/45 px-3 py-2.5"
        >
          <span
            aria-hidden
            className="h-10 w-10 shrink-0 animate-pulse rounded-lg bg-black/[0.06] motion-reduce:animate-none"
          />
          <span className="flex min-w-0 flex-1 flex-col gap-2">
            <span
              aria-hidden
              className="h-3 w-2/3 animate-pulse rounded bg-black/[0.06] motion-reduce:animate-none"
            />
            <span
              aria-hidden
              className="h-2.5 w-1/3 animate-pulse rounded bg-black/[0.04] motion-reduce:animate-none"
            />
          </span>
        </li>
      ))}
    </ul>
  );
};
DocumentsSkeleton.displayName = "DocumentsSkeleton";

// ── Inline-viewer (unchanged from previous revision) ─────────────────────

const DOCS_BACK_LABEL_MSG = msg`Torna alla lista documenti`;

const MIME_LABEL_MSG: Readonly<Record<string, ReturnType<typeof msg>>> = {
  "application/pdf": msg`PDF`,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": msg`Word`,
  "application/msword": msg`Word`,
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": msg`Excel`,
  "application/vnd.ms-excel": msg`Excel`,
  "text/csv": msg`CSV`,
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": msg`PowerPoint`,
  "application/vnd.ms-powerpoint": msg`PowerPoint`,
  "application/vnd.oasis.opendocument.presentation": msg`Presentazione`,
};

/**
 * Walk the chat-store messages for the AndoSource whose ``doc_id``
 * matches ``focusId`` AND whose ``source_type`` is ``"web"``. Used to
 * deflect the focused view away from ``ArtifactPane`` (which expects
 * a real document) and into the ``WebPreview`` viewer when the user
 * clicks a web citation chip — root cause of the Jun 9 user report
 * "Documento non disponibile" placeholder showing for every web tab.
 *
 * Returns ``null`` for non-web matches so the caller falls through to
 * the canonical document path.
 */
function findWebSource(
  messagesByConversation: Record<string, readonly { sources?: readonly AndoSource[] }[]>,
  focusId: string,
): AndoSource | null {
  for (const list of Object.values(messagesByConversation)) {
    for (const msg of list) {
      const hit = msg.sources?.find((s) => s.doc_id === focusId);
      if (hit && hit.source_type === "web" && hit.url) {
        return hit;
      }
    }
  }
  return null;
}

/**
 * Web-source branch of ``FocusedDocumentView``. Extracted as a
 * separate component so ``useEditSession`` (called by the document
 * branch) never fires for web ids — Rule of Hooks-safe split.
 */
const FocusedWebView: React.FC<{
  webSource: AndoSource;
  focusId: string;
  messageId: string | null;
  onBack: () => void;
}> = ({ webSource, focusId, messageId, onBack }) => {
  const { i18n } = useLingui();
  const webPayload: WebPayload = {
    url: webSource.url ?? "",
    title: webSource.source_file,
    snippet: webSource.content,
    publishedDate: webSource.published_date,
  };
  const webTitle =
    webSource.source_file?.trim() ||
    (() => {
      try {
        return new URL(webPayload.url).hostname.replace(/^www\./, "");
      } catch {
        return i18n._(msg`Risultato web`);
      }
    })();

  // Active context — the user has a web source opened inline; surface
  // it to the agent as a ``source_citation`` (its kind on the wire) so
  // follow-up questions "summarise this article" / "draft a reply
  // citing this" land on the right source.
  useActiveContextScope({
    kind: "source_citation",
    id: focusId,
    title: webTitle,
    summary: webSource.content?.slice(0, 200) || undefined,
    meta: {
      source_kind: "web",
      ...(webPayload.url ? { url: webPayload.url } : {}),
      ...(webPayload.publishedDate ? { published_date: webPayload.publishedDate } : {}),
    },
  });
  return (
    <FocusedViewLayout
      tabLabel={i18n._(msg`Documenti`)}
      eyebrowSuffix={i18n._(msg`Web`)}
      title={webTitle}
      icon={<FileText className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />}
      backAriaLabel={i18n._(DOCS_BACK_LABEL_MSG)}
      onBack={onBack}
      messageId={messageId}
      focusId={focusId}
    >
      <WebPreview payload={webPayload} />
    </FocusedViewLayout>
  );
};

/**
 * Document-artefact branch — original behaviour (PDF / docx / xlsx /
 * pptx via ``ArtifactPane`` + ``useEditSession``).
 */
const FocusedArtifactView: React.FC<{
  focusId: string;
  messageId: string | null;
  onBack: () => void;
}> = ({ focusId, messageId, onBack }) => {
  const { i18n } = useLingui();
  const editor = useEditSession(focusId);
  const title = editor.data?.filename ?? i18n._(msg`Documento`);
  const mime = editor.data?.mime_type;
  const eyebrowSuffix = mime && MIME_LABEL_MSG[mime] ? i18n._(MIME_LABEL_MSG[mime]) : undefined;

  // Active context — the user has a document opened inline. The agent
  // can then answer "summarise this", "draft a follow-up email about
  // it", "what changed in v3" without the user having to spell out the
  // filename.
  useActiveContextScope(
    editor.data
      ? {
          kind: "document",
          id: focusId,
          title,
          summary: eyebrowSuffix,
          meta: {
            ...(mime ? { mime_type: mime } : {}),
            ...(editor.data.filename ? { filename: editor.data.filename } : {}),
          },
        }
      : null,
  );
  return (
    <FocusedViewLayout
      tabLabel={i18n._(msg`Documenti`)}
      eyebrowSuffix={eyebrowSuffix}
      title={title}
      icon={<FileText className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />}
      backAriaLabel={i18n._(DOCS_BACK_LABEL_MSG)}
      onBack={onBack}
      messageId={messageId}
      focusId={focusId}
    >
      <ArtifactPane docId={focusId} onAfterDraftReject={onBack} />
    </FocusedViewLayout>
  );
};

const FocusedDocumentView: React.FC<{
  focusId: string;
  messageId: string | null;
  onBack: () => void;
}> = ({ focusId, messageId, onBack }) => {
  // Jun 9, 2026 — web-source deflection. The SourceRail (and
  // ``useSourceAutoOpen``) navigates here with ``?focus=<doc_id>`` for
  // every source type, including web. Without this lookup the user
  // landed on ``ArtifactPane`` which 404'd ("Documento non disponibile"
  // — user report Jun 9). When the focused id resolves to a web
  // source in the chat store, render ``WebPreview`` instead. The
  // split into ``FocusedWebView`` / ``FocusedArtifactView`` keeps
  // ``useEditSession`` from firing for web ids (Rule of Hooks).
  const webSource = useChatStore((s) => findWebSource(s.messagesByConversation, focusId));
  if (webSource !== null) {
    return (
      <FocusedWebView
        webSource={webSource}
        focusId={focusId}
        messageId={messageId}
        onBack={onBack}
      />
    );
  }
  return <FocusedArtifactView focusId={focusId} messageId={messageId} onBack={onBack} />;
};
